/**
 * Sign in.
 *
 * There is no "create account" link and no password reset that a stranger can
 * start, because access is a deliverable of a confirmed agency agreement. A
 * captain who cannot get in contacts ABG and is re-invited; that is the whole
 * recovery path, and saying so plainly beats a dead-end "Forgot password?".
 *
 * The TOTP field appears only after the server asks for it (428), rather than
 * always being on screen. Most captains will never enable 2FA, and a code box
 * they must leave blank reads like a form they are filling in wrongly.
 */

import { useState, type FormEvent } from 'react'
import { Field, Notice } from '@abg/ui'
import { ApiError, OfflineError } from '../lib/api'
import { useAuth } from '../lib/auth'

export function LoginPage() {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [needsTotp, setNeedsTotp] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email.trim(), password, needsTotp ? totpCode : undefined)
    } catch (caught) {
      if (caught instanceof OfflineError) {
        setError('No connection. Signing in needs a network — the rest of the app works offline.')
      } else if (caught instanceof ApiError && caught.status === 428) {
        // The server is telling us 2FA is on for this account. Not a failure.
        setNeedsTotp(true)
        setError(null)
      } else if (caught instanceof ApiError) {
        setError(caught.message)
      } else {
        setError('Could not sign in. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={submit}>
        <div>
          <div className="auth-brand">Superyacht Services</div>
          <h1>Anchoring</h1>
        </div>

        {error && <Notice tone="alert">{error}</Notice>}

        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            required
            disabled={needsTotp}
          />
        </Field>

        <Field label="Password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={needsTotp}
          />
        </Field>

        {needsTotp && (
          <Field label="Authenticator code" hint="Six digits from your authenticator app.">
            <input
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              // Autofocused because the captain has just been asked for it and
              // the keyboard should already be up.
              autoFocus
              required
            />
          </Field>
        )}

        <button type="submit" className="btn btn-lg btn-block" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="caption">
          Access is by invitation. If you have lost your access, contact your agent
          and they will send a new invite link.
        </p>
      </form>
    </div>
  )
}
